/**
 * PROTOTYPE dashboard fixtures (Phase P1a). Static, hand-authored sample
 * data so the vibrant learner + guardian dashboards can be built and
 * verified FE-first. Replaced by real API reads in the F1 backend slice
 * (Iron Rule #1). Nothing here is a source of truth.
 */
import {
  Award,
  BellRing,
  BookOpen,
  Calculator,
  CalendarClock,
  ClipboardList,
  FlaskConical,
  Globe2,
  GraduationCap,
  Library,
  MessagesSquare,
  MonitorPlay,
  NotebookPen,
  ReceiptText,
  Trophy,
  Users,
  Wallet,
  type LucideIcon,
} from "lucide-react";
import type { JoyKey } from "./joy";

export interface LearnerProfile {
  className: string;
  section: string;
  rollNo: number;
  motto: string;
  streakDays: number;
}

export interface ScheduleItem {
  period: number;
  subject: string;
  time: string;
  icon: LucideIcon;
  color: JoyKey;
}

export interface ProgressBar {
  label: string;
  value: number;
  color: JoyKey;
}

export interface AssignmentInfo {
  title: string;
  topic: string;
  dueLabel: string;
  daysLeft: number;
}

export interface ExploreItem {
  label: string;
  icon: LucideIcon;
  color: JoyKey;
}

export interface OverviewStat {
  label: string;
  value: string;
  icon: LucideIcon;
  color: JoyKey;
}

export interface UpdateItem {
  title: string;
  detail: string;
  when: string;
  icon: LucideIcon;
  color: JoyKey;
}

export interface CommItem {
  label: string;
  icon: LucideIcon;
  color: JoyKey;
}

export const LEARNER: LearnerProfile = {
  className: "Class 8",
  section: "A",
  rollNo: 23,
  motto: "Keep learning, keep growing.",
  streakDays: 12,
};

export const SCHEDULE: ScheduleItem[] = [
  { period: 1, subject: "Maths", time: "8:00 AM", icon: Calculator, color: "indigo" },
  { period: 2, subject: "Science", time: "9:00 AM", icon: FlaskConical, color: "green" },
  { period: 3, subject: "English", time: "10:00 AM", icon: BookOpen, color: "amber" },
  { period: 4, subject: "History", time: "11:00 AM", icon: Globe2, color: "rose" },
  { period: 5, subject: "Computers", time: "12:00 PM", icon: MonitorPlay, color: "sky" },
];

export const ASSIGNMENT: AssignmentInfo = {
  title: "Science Project",
  topic: "Our Environment",
  dueLabel: "Due 24 May",
  daysLeft: 3,
};

export const PROGRESS_OVERALL = 85;

export const PROGRESS_BARS: ProgressBar[] = [
  { label: "Homework", value: 90, color: "green" },
  { label: "Class Tests", value: 78, color: "amber" },
  { label: "Attendance", value: 92, color: "sky" },
];

export const EXPLORE: ExploreItem[] = [
  { label: "Study Material", icon: NotebookPen, color: "indigo" },
  { label: "Online Classes", icon: MonitorPlay, color: "green" },
  { label: "Library", icon: Library, color: "amber" },
  { label: "Achievements", icon: Trophy, color: "pink" },
];

export const ACHIEVEMENT = {
  badge: "Top Performer",
  headline: "You earned a new badge!",
  detail: "3rd in class this week — keep the streak alive.",
};

/* ── Guardian side ──────────────────────────────────────────────────── */

export const OVERVIEW_STATS: OverviewStat[] = [
  { label: "Attendance", value: "92%", icon: ClipboardList, color: "green" },
  { label: "Academic", value: "85%", icon: GraduationCap, color: "indigo" },
  { label: "Grade", value: "A", icon: Award, color: "violet" },
  { label: "Assignments", value: "12/15", icon: NotebookPen, color: "amber" },
];

export const UPDATES: UpdateItem[] = [
  {
    title: "Attendance marked",
    detail: "Aarav was present today.",
    when: "Today, 8:30 AM",
    icon: ClipboardList,
    color: "green",
  },
  {
    title: "New assignment",
    detail: "Science Project assigned.",
    when: "Yesterday, 4:20 PM",
    icon: NotebookPen,
    color: "indigo",
  },
  {
    title: "Fee reminder",
    detail: "Term 2 fee due on 30 May.",
    when: "Yesterday, 11:00 AM",
    icon: BellRing,
    color: "amber",
  },
];

export const FEE = {
  amount: "₹12,500",
  dueLabel: "Due 30 May 2025",
};

export const COMMUNICATION: CommItem[] = [
  { label: "Message Teacher", icon: MessagesSquare, color: "indigo" },
  { label: "Report Card", icon: ReceiptText, color: "green" },
  { label: "School Notice", icon: BellRing, color: "amber" },
  { label: "Parent Meet", icon: Users, color: "pink" },
];

/** AI-assist starter prompts, role-aware. */
export const ASK_PROMPTS: Record<"student" | "guardian", string[]> = {
  student: [
    "What's due this week?",
    "Explain today's Science topic",
    "Quiz me on Maths chapter 4",
    "When is my next test?",
  ],
  guardian: [
    "How is Aarav doing this term?",
    "Any pending fees?",
    "Summarise this week's updates",
    "Book a parent–teacher meeting",
  ],
};

export const FEE_ICON = Wallet;
export const SCHEDULE_HEADER_ICON = CalendarClock;
